import type {
  AssistantActiveRouteContextEnvelope,
  AssistantPolicyEvent,
  AssistantPolicyEventResult,
  AssistantResponseAction,
} from '@triedge/domain-core';
import type {
  ReportGenerationAdvancedVisualizationActiveView,
  ReportGenerationAdvancedVisualizationBodySurface,
  ReportGenerationAdvancedVisualizationChartType,
  ReportGenerationAdvancedVisualizationPolicy,
  ReportGenerationExportFormat,
  ReportGenerationFieldOption,
  ReportGenerationFieldType,
  ReportGenerationFilterCondition,
  ReportGenerationTemplateView,
  ReportGenerationWorkspaceModel,
  ReportGenerationWorkspaceTab,
} from './types';

export const RUN_REPORTS_ASSISTANT_CONTEXT_KEY = 'runReportsWorkspace';
export const RUN_REPORTS_ASSISTANT_PATHNAME = '/school/run-reports';

export type TemplateSource = 'gallery' | 'custom';

export type RunReportsTemplateSummary = {
  id: string;
  name: string;
  description: string;
  source: TemplateSource;
  moduleLabel: string;
  fieldCount: number;
  entityCount: number;
  visibility?: string | null;
  isScheduled?: boolean | null;
};

export type RunReportsFieldSummary = {
  fieldId: string;
  fieldLabel: string;
  fieldType: ReportGenerationFieldType;
  entityId: string;
  entityLabel: string;
  moduleId: string;
  moduleLabel: string;
  sortable: boolean;
  selected: boolean;
  hidden: boolean;
};

export type RunReportsHistorySummary = {
  id: string;
  templateName: string;
  status: string;
  runDate: string;
  format: string;
  rowCount: number | null;
  artifactCount: number;
  reusable: boolean;
};

export type RunReportsDraftOperation =
  | 'open-tab'
  | 'apply-template'
  | 'start-new-report'
  | 'set-title'
  | 'select-fields'
  | 'toggle-field'
  | 'add-filter'
  | 'update-filter'
  | 'remove-filter'
  | 'set-date-range'
  | 'set-sort'
  | 'clear-selections'
  | 'set-visual'
  | 'save-template'
  | 'schedule-report'
  | 'run-export'
  | 'run-export-bundle'
  | 'load-history'
  | 'rerun-history'
  | 'download-history';

const RUN_REPORTS_FIELD_INTENT_IDS = [
  'academic_year',
  'amount_paid',
  'amount_due',
  'balance_remaining',
  'due_date',
  'family_name',
  'payment_status',
  'plan_name',
  'student_full_name',
] as const;

export type RunReportsFieldIntent = typeof RUN_REPORTS_FIELD_INTENT_IDS[number];

export const RUN_REPORTS_TUITION_REVIEW_FIELD_IDS = [
  'family_name',
  'student_full_name',
  'due_date',
  'balance_remaining',
  'amount_due',
  'payment_status',
  'plan_name',
  'academic_year',
] as const;

export type RunReportsDraftPayload = {
  draftOperation?: RunReportsDraftOperation;
  tab?: ReportGenerationWorkspaceTab;
  templateView?: ReportGenerationTemplateView;
  templateId?: string;
  source?: TemplateSource;
  title?: string;
  fieldId?: string;
  fieldIntent?: RunReportsFieldIntent;
  fieldLabel?: string;
  fieldIds?: string[];
  conditionId?: string;
  filter?: {
    fieldId?: string;
    fieldIntent?: RunReportsFieldIntent;
    fieldLabel?: string;
    operator?: string;
    value?: string;
  };
  dateRange?: {
    preset?: string;
    from?: string;
    to?: string;
  };
  sort?: {
    fieldId?: string;
    fieldIntent?: RunReportsFieldIntent;
    fieldLabel?: string;
    direction?: 'asc' | 'desc';
  };
  bodySurface?: ReportGenerationAdvancedVisualizationBodySurface;
  view?: ReportGenerationAdvancedVisualizationActiveView;
  chartType?: ReportGenerationAdvancedVisualizationChartType;
  format?: ReportGenerationExportFormat;
  visibility?: 'private' | 'shared';
  historyEntryId?: string;
};

export type RunReportsAssistantSnapshot = {
  kind: 'run-reports-workspace';
  pathname: string;
  title: string;
  activeTab: ReportGenerationWorkspaceTab;
  templateView: ReportGenerationTemplateView;
  bodySurface: ReportGenerationAdvancedVisualizationBodySurface;
  visualizationView: ReportGenerationAdvancedVisualizationActiveView;
  visualizationChartType: ReportGenerationAdvancedVisualizationChartType | null;
  selectedTemplateId: string | null;
  selectedTemplateSource: TemplateSource | null;
  selectedTemplateName: string | null;
  selectedEntities: Array<{ id: string; label: string; moduleLabel: string }>;
  selectedFields: RunReportsFieldSummary[];
  hiddenColumns: string[];
  filters: Array<Pick<ReportGenerationFilterCondition, 'id' | 'fieldId' | 'fieldLabel' | 'operator' | 'value' | 'fieldType'>>;
  dateRange: {
    preset: string;
    from: string;
    to: string;
  };
  sorts: Array<{ fieldId: string; fieldLabel: string; direction: 'asc' | 'desc' }>;
  availableFields: RunReportsFieldSummary[];
  galleryTemplates: RunReportsTemplateSummary[];
  myTemplates: RunReportsTemplateSummary[];
  historyEntries: RunReportsHistorySummary[];
  joins: string[];
  warnings: string[];
  previewColumnCount: number;
  previewRowCount: number;
  totalRowCount: number;
  canPreview: boolean;
  runnable: boolean;
  visualPolicy: ReportGenerationAdvancedVisualizationPolicy | null;
  chartOptions: Array<{
    id: string;
    label: string;
    chartType: ReportGenerationAdvancedVisualizationChartType;
    pointCount: number;
  }>;
  lastActionFeedback: string | null;
  hasActiveSchedule: boolean;
};

export type RunReportsWorkspaceActionIntent =
  | 'apply-aging-template'
  | 'select-tuition-review-columns'
  | 'add-collection-risk-filter'
  | 'set-aging-visual'
  | 'run-export-bundle'
  | 'save-template'
  | 'schedule-report';

export type RunReportsWorkspacePromptActionResolution = Readonly<{
  intent: RunReportsWorkspaceActionIntent;
  action: AssistantResponseAction;
  payload: RunReportsDraftPayload;
  matchedSignals: readonly string[];
  score: number;
}>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const stableHash = (value: string): string => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const buildRouteRevision = (snapshot: RunReportsAssistantSnapshot): string =>
  stableHash(JSON.stringify({
    title: snapshot.title,
    activeTab: snapshot.activeTab,
    templateView: snapshot.templateView,
    bodySurface: snapshot.bodySurface,
    visualizationView: snapshot.visualizationView,
    visualizationChartType: snapshot.visualizationChartType,
    selectedTemplateId: snapshot.selectedTemplateId,
    selectedTemplateSource: snapshot.selectedTemplateSource,
    selectedEntities: snapshot.selectedEntities.map((entity) => entity.id),
    selectedFields: snapshot.selectedFields.map((field) => field.fieldId),
    hiddenColumns: snapshot.hiddenColumns,
    filters: snapshot.filters,
    dateRange: snapshot.dateRange,
    sorts: snapshot.sorts,
    previewRowCount: snapshot.previewRowCount,
    totalRowCount: snapshot.totalRowCount,
    runnable: snapshot.runnable,
  }));

const receiptPresentation = ({
  accepted,
  titleText,
  summary,
  meta,
}: {
  accepted: boolean;
  titleText: string;
  summary: string;
  meta?: string | null;
}): NonNullable<AssistantPolicyEventResult['presentation']> => ({
  version: '1.0.0',
  routeFamily: 'run-reports',
  module: 'tuition',
  responseKind: 'draft_creator',
  sections: [
    {
      type: 'confirmation',
      tone: accepted ? 'success' : 'warning',
      titleText,
      summary,
      meta: meta ?? null,
    },
    {
      type: 'related_links',
      title: 'Useful actions',
      groups: [{
        id: 'run-reports-receipt-actions',
        label: 'Run Reports',
        items: [{
          id: 'run-reports-open-workspace-receipt',
          href: RUN_REPORTS_ASSISTANT_PATHNAME,
          label: 'Open Run Reports workspace',
          meta: 'Route action',
          preview: accepted
            ? 'Review the updated report draft before export, save, or schedule.'
            : 'Open the source workspace and refresh the visible route context.',
          external: false,
        }],
      }],
    },
  ],
});

const summarizeTemplate = (
  template: ReportGenerationWorkspaceModel['galleryTemplates'][number],
  source: TemplateSource,
): RunReportsTemplateSummary => ({
  id: template.id,
  name: template.name,
  description: template.description,
  source,
  moduleLabel: template.moduleLabel,
  fieldCount: template.fields.length,
  entityCount: template.entities.length,
  visibility: template.visibility ?? null,
  isScheduled: template.isScheduled ?? null,
});

const entitySummaryById = (
  model: ReportGenerationWorkspaceModel,
): Map<string, { id: string; label: string; moduleLabel: string }> => {
  const entries = new Map<string, { id: string; label: string; moduleLabel: string }>();
  for (const entity of model.modules.flatMap((module) => module.entities)) {
    entries.set(entity.id, {
      id: entity.id,
      label: entity.label,
      moduleLabel: entity.moduleLabel,
    });
  }
  return entries;
};

const summarizeField = (
  field: ReportGenerationFieldOption,
  selectedFieldIds: Set<string>,
  hiddenColumns: Set<string>,
): RunReportsFieldSummary => ({
  fieldId: field.fieldId,
  fieldLabel: field.fieldLabel,
  fieldType: field.fieldType,
  entityId: field.entityId,
  entityLabel: field.entityLabel,
  moduleId: field.moduleId,
  moduleLabel: field.moduleLabel,
  sortable: field.sortable === true,
  selected: selectedFieldIds.has(field.fieldId),
  hidden: hiddenColumns.has(field.fieldId),
});

const selectedTemplateName = (
  model: ReportGenerationWorkspaceModel,
): string | null => {
  const { selectedTemplateId, selectedTemplateSource } = model.filterState;
  if (!selectedTemplateId || !selectedTemplateSource) {
    return null;
  }
  const templates = selectedTemplateSource === 'custom'
    ? model.myTemplates
    : model.galleryTemplates;
  return templates.find((template) => template.id === selectedTemplateId)?.name ?? null;
};

export const buildRunReportsAssistantSnapshot = (
  model: ReportGenerationWorkspaceModel,
): RunReportsAssistantSnapshot => {
  const selectedFieldIds = new Set(model.filterState.filterState.selectedFields);
  const hiddenColumns = new Set(model.workspace.hiddenColumns);
  const entities = entitySummaryById(model);
  const selectedEntities = model.filterState.filterState.selectedEntities.map((entityId) =>
    entities.get(entityId) ?? { id: entityId, label: entityId, moduleLabel: 'Run Reports' },
  );
  const policy = model.advancedVisualization?.chartVisualization?.policy ?? null;
  const chartOptions = model.advancedVisualization?.chartVisualization?.chartOptions ?? [];
  const defaultView = policy?.defaultView === 'advanced' ? 'advanced' : 'chart';
  return {
    kind: 'run-reports-workspace',
    pathname: RUN_REPORTS_ASSISTANT_PATHNAME,
    title: model.resolvedTitle,
    activeTab: model.workspace.activeTab,
    templateView: model.workspace.templateView,
    bodySurface: model.bodySurface ?? 'table',
    visualizationView: model.visualizationView ?? defaultView,
    visualizationChartType: model.visualizationChartType ?? policy?.defaultChartType ?? null,
    selectedTemplateId: model.filterState.selectedTemplateId,
    selectedTemplateSource: model.filterState.selectedTemplateSource,
    selectedTemplateName: selectedTemplateName(model),
    selectedEntities,
    selectedFields: model.availableFields
      .filter((field) => selectedFieldIds.has(field.fieldId))
      .map((field) => summarizeField(field, selectedFieldIds, hiddenColumns)),
    hiddenColumns: [...model.workspace.hiddenColumns],
    filters: model.filterState.filterState.conditions.map((condition) => ({
      id: condition.id,
      fieldId: condition.fieldId,
      fieldLabel: condition.fieldLabel,
      operator: condition.operator,
      value: condition.value,
      fieldType: condition.fieldType,
    })),
    dateRange: { ...model.filterState.filterState.dateRange },
    sorts: model.filterState.filterState.sorts.map((sort) => ({ ...sort })),
    availableFields: model.availableFields.map((field) =>
      summarizeField(field, selectedFieldIds, hiddenColumns),
    ),
    galleryTemplates: model.galleryTemplates.map((template) => summarizeTemplate(template, 'gallery')),
    myTemplates: model.myTemplates.map((template) => summarizeTemplate(template, 'custom')),
    historyEntries: model.historyEntries.map((entry) => ({
      id: entry.id,
      templateName: entry.templateName,
      status: entry.status,
      runDate: entry.runDate,
      format: entry.format,
      rowCount: entry.rowCount ?? null,
      artifactCount: entry.artifactRefs.length,
      reusable: entry.status === 'Completed' || Boolean(entry.templateId),
    })),
    joins: [...model.joins],
    warnings: [...model.previewWarnings],
    previewColumnCount: model.previewColumns.length,
    previewRowCount: model.previewRows.length,
    totalRowCount: model.totalRowCount,
    canPreview: model.canPreview,
    runnable:
      model.filterState.filterState.selectedEntities.length > 0 &&
      model.filterState.filterState.selectedFields.length > 0,
    visualPolicy: policy,
    chartOptions: chartOptions.map((option) => ({
      id: option.id,
      label: option.label,
      chartType: option.chartType,
      pointCount: option.pointCount,
    })),
    lastActionFeedback: model.lastActionFeedback,
    hasActiveSchedule: model.hasActiveSchedule,
  };
};

export const buildRunReportsActiveRouteContextEnvelope = (
  snapshot: RunReportsAssistantSnapshot,
  options?: { contextNamespace?: string },
): AssistantActiveRouteContextEnvelope => {
  const revision = buildRouteRevision(snapshot);
  const now = Date.now();
  const namespace = options?.contextNamespace?.trim() || 'run-reports';
  const contextRef = `${namespace}:active-route:run-reports:${revision}`;
  const selectedFieldCount = snapshot.selectedFields.length;
  const filterCount = snapshot.filters.length;
  const templateCount = snapshot.galleryTemplates.length + snapshot.myTemplates.length;
  return {
    schemaVersion: '1.0.0',
    contextRef,
    routeFamily: 'run-reports',
    pathname: snapshot.pathname,
    revision,
    facets: [
      {
        kind: 'templates',
        label: 'Report templates',
        summary: `${templateCount} templates available; selected template is ${snapshot.selectedTemplateName ?? 'none'}.`,
        available: true,
        itemCount: templateCount,
        state: {
          selectedTemplateId: snapshot.selectedTemplateId,
          selectedTemplateSource: snapshot.selectedTemplateSource,
          selectedTemplateName: snapshot.selectedTemplateName,
        },
      },
      {
        kind: 'filters',
        label: 'Report filters',
        summary: `${filterCount} active filters and ${snapshot.sorts.length} sort rules.`,
        available: true,
        itemCount: filterCount,
        state: {
          filters: snapshot.filters,
          dateRange: snapshot.dateRange,
          sorts: snapshot.sorts,
        },
      },
      {
        kind: 'table',
        label: 'Preview table',
        summary: `${snapshot.previewRowCount} visible preview rows across ${snapshot.previewColumnCount} columns.`,
        available: true,
        itemCount: snapshot.previewRowCount,
        state: {
          previewRowCount: snapshot.previewRowCount,
          previewColumnCount: snapshot.previewColumnCount,
          totalRowCount: snapshot.totalRowCount,
        },
      },
      {
        kind: 'chart',
        label: 'Chart options',
        summary: `${snapshot.chartOptions.length} chart options available for the active report draft.`,
        available: snapshot.chartOptions.length > 0,
        itemCount: snapshot.chartOptions.length,
        state: {
          bodySurface: snapshot.bodySurface,
          visualizationView: snapshot.visualizationView,
          visualizationChartType: snapshot.visualizationChartType,
          chartOptions: snapshot.chartOptions,
        },
      },
      {
        kind: 'selection',
        label: 'Report selection',
        summary: `${snapshot.selectedEntities.length} entities and ${selectedFieldCount} fields selected.`,
        available: true,
        itemCount: selectedFieldCount,
        state: {
          selectedEntities: snapshot.selectedEntities,
          selectedFields: snapshot.selectedFields,
          hiddenColumns: snapshot.hiddenColumns,
        },
      },
      {
        kind: 'history',
        label: 'Run history',
        summary: `${snapshot.historyEntries.length} history entries are available for hydration or rerun.`,
        available: true,
        itemCount: snapshot.historyEntries.length,
        state: {
          historyEntries: snapshot.historyEntries,
        },
      },
      {
        kind: 'export',
        label: 'Export readiness',
        summary: snapshot.runnable
          ? 'The active report definition is exportable.'
          : 'The active report definition needs at least one entity and one field before export.',
        available: snapshot.runnable,
        state: {
          runnable: snapshot.runnable,
          warnings: snapshot.warnings,
        },
      },
      {
        kind: 'artifact-workspace',
        label: 'Report workspace',
        summary: snapshot.hasActiveSchedule
          ? 'The report workspace includes an active schedule.'
          : 'The report workspace is available for report, spreadsheet, and chart artifacts.',
        available: true,
        state: {
          activeTab: snapshot.activeTab,
          hasActiveSchedule: snapshot.hasActiveSchedule,
        },
      },
      {
        kind: 'visualization3d',
        label: 'Advanced visualization',
        summary: snapshot.visualPolicy
          ? 'Advanced visualization policy is available for this report draft.'
          : 'Advanced visualization policy is not available for this report draft.',
        available: Boolean(snapshot.visualPolicy),
        itemCount: snapshot.chartOptions.length,
      },
    ],
    artifactSources: [
      {
        artifactType: 'report',
        label: 'Report definition',
        sourceRef: `active-route:${contextRef}:report-definition`,
        routeScoped: true,
        actions: [
          {
            id: 'run-reports-open-workspace',
            kind: 'open_workspace',
            label: 'Open workspace',
            sourceRef: `active-route:${contextRef}:workspace`,
            policyEventType: 'open_workspace',
          },
          {
            id: 'run-reports-hydrate-history',
            kind: 'hydrate_history',
            label: 'Hydrate from history',
            sourceRef: `active-route:${contextRef}:history`,
            policyEventType: 'hydrate_history',
          },
        ],
      },
      {
        artifactType: 'spreadsheet',
        label: 'Spreadsheet export',
        sourceRef: `active-route:${contextRef}:spreadsheet`,
        routeScoped: true,
        actions: [
          {
            id: 'run-reports-export-google-sheets',
            kind: 'export_google_sheets',
            label: 'Export to Google Sheets',
            sourceRef: `active-route:${contextRef}:spreadsheet`,
            policyEventType: 'export_google_sheets',
          },
        ],
      },
      {
        artifactType: 'chart',
        label: 'Chart projection',
        sourceRef: `active-route:${contextRef}:chart`,
        routeScoped: true,
        actions: [],
      },
    ],
    capabilityConstraints: {
      allowedCapabilityIds: [],
      disabledCapabilityIds: [],
      allowedPolicyEventTypes: [
        'route_local_action',
        'open_workspace',
        'hydrate_history',
        'export_google_sheets',
      ],
      disabledPolicyEventTypes: [],
      allowLocalMutation: true,
      allowArtifactWorkspaceOpen: true,
      allowExport: snapshot.runnable,
    },
    visibleStateSummary: {
      title: snapshot.title,
      summary: `${snapshot.title}: ${snapshot.selectedEntities.length} entities, ${selectedFieldCount} fields, ${filterCount} filters, and ${snapshot.previewRowCount} visible rows.`,
      metrics: [
        { label: 'Fields', value: String(selectedFieldCount) },
        { label: 'Filters', value: String(filterCount) },
        { label: 'Preview rows', value: String(snapshot.previewRowCount) },
      ],
    },
    staleness: {
      state: 'fresh',
      capturedAtEpochMs: now,
      expiresAtEpochMs: now + 5 * 60 * 1000,
    },
    sourceRefs: [
      {
        sourceRef: `active-route:${contextRef}:visible-state`,
        kind: 'route-visible-state',
        label: 'Run Reports visible state',
      },
    ],
  };
};

export const readRunReportsAssistantSnapshot = (
  context: Record<string, unknown> | null | undefined,
): RunReportsAssistantSnapshot | null => {
  const candidate = isRecord(context?.[RUN_REPORTS_ASSISTANT_CONTEXT_KEY])
    ? context?.[RUN_REPORTS_ASSISTANT_CONTEXT_KEY]
    : isRecord(context?.routeContext) && isRecord(context.routeContext[RUN_REPORTS_ASSISTANT_CONTEXT_KEY])
      ? context.routeContext[RUN_REPORTS_ASSISTANT_CONTEXT_KEY]
      : null;
  if (!isRecord(candidate) || candidate.kind !== 'run-reports-workspace') {
    return null;
  }
  return candidate as RunReportsAssistantSnapshot;
};

const workspaceContextPhraseFromModel = (
  model: ReportGenerationWorkspaceModel,
): string =>
  `"${model.resolvedTitle}" with ${model.filterState.filterState.selectedEntities.length} entities, ${model.filterState.filterState.selectedFields.length} fields, ${model.filterState.filterState.conditions.length} filters, and ${model.previewRows.length.toLocaleString()} preview rows`;

const reportBuilderConfirmation = (
  change: string,
  state: string,
): string =>
  `Run Reports workspace updated: ${change}. Workspace state: ${state}.`;

const actionPayload = (
  action: AssistantResponseAction,
): RunReportsDraftPayload | null => (
  isRecord(action.payload) ? action.payload as RunReportsDraftPayload : null
);

const runReportsDraftAction = (
  id: string,
  label: string,
  payload: RunReportsDraftPayload,
): AssistantResponseAction => ({
  id,
  label,
  type: 'draft',
  kind: 'draft',
  actionCategory: 'run-reports-workspace-draft',
  payload,
});

const normalizeRunReportsPrompt = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const hasPromptSignal = (text: string, pattern: RegExp): boolean => pattern.test(text);

const promptSignals = (
  text: string,
  entries: readonly [string, RegExp][],
): string[] => entries
  .filter(([, pattern]) => hasPromptSignal(text, pattern))
  .map(([signal]) => signal);

type RunReportsWorkspaceActionIntentDefinition = Readonly<{
  intent: RunReportsWorkspaceActionIntent;
  actionId: string;
  label: string;
  payload: RunReportsDraftPayload;
  priority: number;
  match: (text: string) => readonly string[];
}>;

const RUN_REPORTS_WORKSPACE_ACTION_INTENTS = [
  {
    intent: 'select-tuition-review-columns',
    actionId: 'run-reports-select-columns',
    label: 'Apply columns',
    payload: {
      draftOperation: 'select-fields',
      fieldIds: [...RUN_REPORTS_TUITION_REVIEW_FIELD_IDS],
    },
    priority: 120,
    match: (text) => {
      const signals = promptSignals(text, [
        ['column-control', /\b(columns?|fields?|visible columns?|selected fields?)\b/],
        ['selection-verb', /\b(set|select|choose|tune|apply|use|make|keep|include|configure)\b/],
        ['report-scope', /\b(aging|tuition|report|template|draft|workspace|run reports?)\b/],
        ['tuition-review-bundle', /\b(balance|balances|due dates?|student names?|payment status|plans?)\b/],
      ]);
      const hasColumnControl = signals.includes('column-control');
      const hasSelectionVerb = signals.includes('selection-verb');
      const hasReportScope = signals.includes('report-scope');
      const hasReviewBundle = signals.includes('tuition-review-bundle')
        && /\bdue dates?\b/.test(text)
        && /\bstudent/.test(text);
      return hasSelectionVerb && hasReportScope && (hasColumnControl || hasReviewBundle)
        ? signals
        : [];
    },
  },
  {
    intent: 'add-collection-risk-filter',
    actionId: 'run-reports-add-risk-filter',
    label: 'Add filters',
    payload: {
      draftOperation: 'add-filter',
      filter: {
        fieldId: 'balance_remaining',
        fieldIntent: 'balance_remaining',
        fieldLabel: 'Balance remaining',
        operator: 'gt',
        value: '0',
      },
    },
    priority: 110,
    match: (text) => {
      const signals = promptSignals(text, [
        ['filter-control', /\b(filters?|condition|only|where|greater than|more than|open balance|collection risk)\b/],
        ['balance-field', /\b(balance remaining|remaining balance|open balance|outstanding balance|balance due)\b/],
        ['positive-balance', /\b(greater than zero|more than zero|above zero|gt 0|>\s*0|non zero|nonzero)\b/],
      ]);
      return signals.includes('filter-control') && (signals.includes('balance-field') || signals.includes('positive-balance'))
        ? signals
        : [];
    },
  },
  {
    intent: 'apply-aging-template',
    actionId: 'run-reports-apply-template-aging-report',
    label: 'Apply template',
    payload: {
      draftOperation: 'apply-template',
      templateId: 'aging-report',
      source: 'gallery',
    },
    priority: 90,
    match: (text) => {
      const signals = promptSignals(text, [
        ['template-control', /\b(template|aging report)\b/],
        ['apply-verb', /\b(open|apply|start|load|use)\b/],
        ['aging-scope', /\baging\b/],
        ['column-control', /\b(columns?|fields?)\b/],
      ]);
      return signals.includes('template-control')
        && signals.includes('apply-verb')
        && signals.includes('aging-scope')
        && !signals.includes('column-control')
        ? signals
        : [];
    },
  },
  {
    intent: 'set-aging-visual',
    actionId: 'run-reports-set-aging-visual',
    label: 'Show visual',
    payload: {
      draftOperation: 'set-visual',
      bodySurface: 'visual',
      view: 'chart',
      chartType: 'bar',
      fieldIds: [...RUN_REPORTS_TUITION_REVIEW_FIELD_IDS],
    },
    priority: 80,
    match: (text) => {
      const signals = promptSignals(text, [
        ['visual-control', /\b(charts?|visual|visualization|graph)\b/],
        ['aging-scope', /\b(aging|tuition|payment|balance)\b/],
      ]);
      return signals.length === 2 ? signals : [];
    },
  },
  {
    intent: 'run-export-bundle',
    actionId: 'run-reports-export-bundle',
    label: 'Run export bundle',
    payload: {
      draftOperation: 'run-export-bundle',
    },
    priority: 70,
    match: (text) => {
      const signals = promptSignals(text, [
        ['export-control', /\b(export|download|spreadsheet|csv|xlsx|excel|pdf)\b/],
        ['report-scope', /\b(report|run reports?|draft|definition)\b/],
      ]);
      return signals.length === 2 ? signals : [];
    },
  },
  {
    intent: 'save-template',
    actionId: 'run-reports-save-private-template',
    label: 'Save template',
    payload: {
      draftOperation: 'save-template',
      visibility: 'private',
    },
    priority: 60,
    match: (text) => {
      const signals = promptSignals(text, [
        ['save-control', /\b(save)\b/],
        ['template-control', /\b(template|definition)\b/],
      ]);
      return signals.length === 2 ? signals : [];
    },
  },
  {
    intent: 'schedule-report',
    actionId: 'run-reports-schedule-daily',
    label: 'Schedule report',
    payload: {
      draftOperation: 'schedule-report',
    },
    priority: 50,
    match: (text) => {
      const signals = promptSignals(text, [
        ['schedule-control', /\b(schedule|daily|recurring|automate)\b/],
        ['report-scope', /\b(report|run reports?|draft|definition)\b/],
      ]);
      return signals.length === 2 ? signals : [];
    },
  },
] as const satisfies readonly RunReportsWorkspaceActionIntentDefinition[];

export const resolveRunReportsWorkspaceDraftActionForPrompt = (
  question: string,
): RunReportsWorkspacePromptActionResolution | null => {
  const text = normalizeRunReportsPrompt(question);
  if (!text) return null;
  const matches = RUN_REPORTS_WORKSPACE_ACTION_INTENTS
    .map((definition) => {
      const matchedSignals = definition.match(text);
      return {
        definition,
        matchedSignals,
        score: definition.priority + (matchedSignals.length * 10),
      };
    })
    .filter((match) => match.matchedSignals.length > 0)
    .sort((left, right) => right.score - left.score);
  const [best] = matches;
  if (!best) return null;
  return {
    intent: best.definition.intent,
    action: runReportsDraftAction(
      best.definition.actionId,
      best.definition.label,
      best.definition.payload,
    ),
    payload: best.definition.payload,
    matchedSignals: best.matchedSignals,
    score: best.score,
  };
};

const normalizeReportFieldToken = (value: string | null | undefined): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

type RunReportsFieldIntentDefinition = {
  readonly fieldIds: readonly string[];
  readonly aliases: readonly string[];
  readonly preferredEntityIds: readonly string[];
  readonly filterFieldIds?: readonly string[];
};

type RunReportsFieldReference = {
  readonly fieldId?: string | null;
  readonly fieldIntent?: RunReportsFieldIntent | null;
  readonly fieldLabel?: string | null;
};

const RUN_REPORTS_FIELD_INTENTS = {
  academic_year: {
    fieldIds: ['academic_year'],
    aliases: ['school_year', 'year'],
    preferredEntityIds: ['installments'],
  },
  amount_paid: {
    fieldIds: ['amount_paid'],
    aliases: ['paid_amount', 'payment_amount', 'installment_paid', 'collected_amount'],
    preferredEntityIds: ['installments', 'payments'],
  },
  amount_due: {
    fieldIds: ['amount_due'],
    aliases: ['current_due', 'due_amount', 'installment_amount'],
    preferredEntityIds: ['installments'],
  },
  balance_remaining: {
    fieldIds: ['balance_remaining'],
    aliases: ['remaining_balance', 'balance_due', 'balance_due_gt_zero', 'current_balance', 'open_balance', 'account_balance', 'outstanding_balance'],
    preferredEntityIds: ['installments'],
  },
  due_date: {
    fieldIds: ['due_date'],
    aliases: ['next_due_date', 'date_due', 'installment_due_date', 'payment_due_date'],
    preferredEntityIds: ['installments'],
  },
  family_name: {
    fieldIds: ['family_name'],
    aliases: ['family', 'family_label'],
    preferredEntityIds: ['families', 'installments'],
  },
  payment_status: {
    fieldIds: ['payment_status'],
    aliases: ['account_status', 'payment_status_p', 'status'],
    preferredEntityIds: ['installments'],
  },
  plan_name: {
    fieldIds: ['plan_name'],
    aliases: ['payment_plan', 'plan', 'tuition_plan', 'plan_label'],
    preferredEntityIds: ['installments'],
  },
  student_full_name: {
    fieldIds: ['first_name', 'last_name'],
    aliases: ['student_name', 'student_names', 'student_full_name', 'full_name'],
    preferredEntityIds: ['students'],
    filterFieldIds: ['first_name', 'last_name'],
  },
} as const satisfies Record<RunReportsFieldIntent, RunReportsFieldIntentDefinition>;

const runReportsFieldIntentEntries = Object.entries(RUN_REPORTS_FIELD_INTENTS) as Array<[
  RunReportsFieldIntent,
  RunReportsFieldIntentDefinition,
]>;

const normalizedIntentTokens = (
  intent: RunReportsFieldIntent,
  definition: RunReportsFieldIntentDefinition,
): string[] => [
  intent,
  ...definition.fieldIds,
  ...definition.aliases,
  ...(definition.filterFieldIds ?? []),
].map(normalizeReportFieldToken).filter(Boolean);

const toFieldReference = (
  input: string | RunReportsFieldReference | null | undefined,
): RunReportsFieldReference => (
  typeof input === 'string' || input == null
    ? { fieldId: input ?? null }
    : input
);

const resolveRunReportsFieldIntent = (
  input: string | RunReportsFieldReference | null | undefined,
): RunReportsFieldIntent | null => {
  const reference = toFieldReference(input);
  const explicitIntent = reference.fieldIntent ?? null;
  if (explicitIntent && RUN_REPORTS_FIELD_INTENT_IDS.includes(explicitIntent)) {
    return explicitIntent;
  }
  const requestedTokens = [reference.fieldId, reference.fieldLabel]
    .map(normalizeReportFieldToken)
    .filter(Boolean);
  if (requestedTokens.length === 0) return null;
  for (const [intent, definition] of runReportsFieldIntentEntries) {
    const intentTokens = normalizedIntentTokens(intent, definition);
    if (requestedTokens.some((token) => intentTokens.includes(token))) {
      return intent;
    }
  }
  return null;
};

const buildFieldOptionCatalog = (
  model: ReportGenerationWorkspaceModel,
): ReportGenerationFieldOption[] => {
  const fields = [...model.availableFields];
  for (const module of model.modules) {
    for (const entity of module.entities) {
      for (const category of entity.visibleCategories) {
        for (const field of category.fields) {
          fields.push({
            entityId: entity.id,
            entityLabel: entity.label,
            fieldId: field.id,
            fieldLabel: field.label,
            fieldType: field.type,
            allowedOperators: undefined,
            sortable: true,
            moduleId: module.moduleId,
            moduleLabel: module.moduleLabel,
          });
        }
      }
    }
  }
  const seen = new Set<string>();
  return fields.filter((field) => {
    const key = `${field.entityId}:${field.fieldId}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const fieldOptionMatchesToken = (
  field: ReportGenerationFieldOption,
  token: string,
): boolean => (
  normalizeReportFieldToken(field.fieldId) === token
  || normalizeReportFieldToken(field.fieldLabel) === token
  || normalizeReportFieldToken(`${field.entityLabel} ${field.fieldLabel}`) === token
);

const fieldLookupTokens = (
  input: string | RunReportsFieldReference | null | undefined,
): string[] => {
  const reference = toFieldReference(input);
  const requested = [reference.fieldId, reference.fieldLabel]
    .map(normalizeReportFieldToken)
    .filter(Boolean);
  const intent = resolveRunReportsFieldIntent(reference);
  const tokens = new Set<string>(requested);
  if (intent) {
    normalizedIntentTokens(intent, RUN_REPORTS_FIELD_INTENTS[intent]).forEach((token) => tokens.add(token));
  }
  return Array.from(tokens);
};

const fieldsForIntent = (
  catalog: ReportGenerationFieldOption[],
  intent: RunReportsFieldIntent,
  mode: 'selection' | 'filter' = 'selection',
): ReportGenerationFieldOption[] => {
  const definition = RUN_REPORTS_FIELD_INTENTS[intent] as RunReportsFieldIntentDefinition;
  const fieldIds: readonly string[] = mode === 'filter' && definition.filterFieldIds
    ? definition.filterFieldIds
    : definition.fieldIds;
  const selected = fieldIds
    .flatMap((fieldId) => catalog.filter((field) => normalizeReportFieldToken(field.fieldId) === normalizeReportFieldToken(fieldId)));
  if (selected.length === 0) {
    return [];
  }
  const preferredEntities = new Set(definition.preferredEntityIds);
  return [...selected].sort((left, right) => {
    const leftPreferred = preferredEntities.has(left.entityId) ? 0 : 1;
    const rightPreferred = preferredEntities.has(right.entityId) ? 0 : 1;
    return leftPreferred - rightPreferred;
  });
};

const findModelField = (
  model: ReportGenerationWorkspaceModel,
  input: string | RunReportsFieldReference | null | undefined,
  mode: 'selection' | 'filter' = 'selection',
): ReportGenerationFieldOption | null => {
  const reference = toFieldReference(input);
  const tokens = fieldLookupTokens(reference);
  if (tokens.length === 0) return null;
  const catalog = buildFieldOptionCatalog(model);
  const intent = resolveRunReportsFieldIntent(reference);
  if (intent) {
    const [field] = fieldsForIntent(catalog, intent, mode);
    if (field) return field;
  }
  for (const token of tokens) {
    const field = catalog.find((candidate) => fieldOptionMatchesToken(candidate, token));
    if (field) return field;
  }
  return null;
};

const resolveModelFields = (
  model: ReportGenerationWorkspaceModel,
  input: string | RunReportsFieldReference | null | undefined,
  mode: 'selection' | 'filter' = 'selection',
): ReportGenerationFieldOption[] => {
  const reference = toFieldReference(input);
  if (!reference.fieldId && !reference.fieldIntent && !reference.fieldLabel) return [];
  const catalog = buildFieldOptionCatalog(model);
  const intent = resolveRunReportsFieldIntent(reference);
  if (intent) {
    const fields = fieldsForIntent(catalog, intent, mode);
    if (fields.length > 0) {
      return uniqueFields(fields);
    }
  }
  const direct = findModelField(model, reference, mode);
  if (direct) {
    return [direct];
  }
  return [];
};

const uniqueFields = (
  fields: ReportGenerationFieldOption[],
): ReportGenerationFieldOption[] => {
  const seen = new Set<string>();
  return fields.filter((field) => {
    const key = `${field.entityId}:${field.fieldId}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const ensureEntitySelected = (
  model: ReportGenerationWorkspaceModel,
  entityId: string,
) => {
  if (!model.filterState.filterState.selectedEntities.includes(entityId)) {
    model.actions.toggleEntity(entityId);
  }
};

const ensureFieldsSelected = (
  model: ReportGenerationWorkspaceModel,
  fields: ReportGenerationFieldOption[],
) => {
  const selectedEntities = new Set(model.filterState.filterState.selectedEntities);
  for (const field of fields) {
    if (!selectedEntities.has(field.entityId)) {
      model.actions.toggleEntity(field.entityId);
      selectedEntities.add(field.entityId);
    }
  }

  const selectedFields = new Set(model.filterState.filterState.selectedFields);
  for (const field of fields) {
    if (!selectedFields.has(field.fieldId)) {
      model.actions.toggleField(field.fieldId);
      selectedFields.add(field.fieldId);
    }
  }
};

const RECOMMENDED_CHART_FIELD_IDS = [
  'due_date',
  'academic_year',
  'payment_status',
  'balance_remaining',
  'amount_due',
  'amount_paid',
  'plan_name',
  'family_name',
] as const;

const ensureRecommendedChartFields = (
  model: ReportGenerationWorkspaceModel,
  requestedFieldIds: readonly string[] = RECOMMENDED_CHART_FIELD_IDS,
): ReportGenerationFieldOption[] => {
  const fields = uniqueFields(requestedFieldIds.flatMap((fieldId) =>
    resolveModelFields(model, fieldId),
  ));
  ensureFieldsSelected(model, fields);
  for (const field of fields) {
    if (model.workspace.hiddenColumns.includes(field.fieldId)) {
      model.actions.toggleHiddenColumn(field.fieldId);
    }
  }
  return fields;
};

const summarizeFieldLabels = (
  fields: readonly ReportGenerationFieldOption[],
  limit = 4,
): string => {
  const labels = [...new Set(fields.map((field) => field.fieldLabel).filter(Boolean))];
  if (labels.length <= limit) {
    return labels.join(', ');
  }
  return `${labels.slice(0, limit).join(', ')}, and more`;
};

const applyCondition = (
  model: ReportGenerationWorkspaceModel,
  input: NonNullable<RunReportsDraftPayload['filter']>,
): { field: ReportGenerationFieldOption } | { blocker: string } => {
  const [field] = resolveModelFields(model, input, 'filter');
  if (!field) {
    return { blocker: 'I need a filterable Run Reports field before I can add that condition. Open the Run Reports workspace and try again.' };
  }
  ensureFieldsSelected(model, [field]);
  const conditionId = `cond-${field.fieldId}`;
  const existing = model.filterState.filterState.conditions.find((condition) =>
    condition.id === conditionId || condition.fieldId === field.fieldId,
  );
  if (!existing) {
    model.actions.addCondition(field);
  }
  model.actions.updateCondition(existing?.id ?? conditionId, {
    operator: input.operator,
    value: input.value,
  });
  return { field };
};

const visualPolicySupports = (
  model: ReportGenerationWorkspaceModel,
  chartType: ReportGenerationAdvancedVisualizationChartType | null | undefined,
): chartType is ReportGenerationAdvancedVisualizationChartType => {
  if (!chartType) return false;
  return Boolean(
    model.advancedVisualization?.chartVisualization?.chartOptions.some((option) =>
      option.chartType === chartType,
    ),
  );
};

export const executeRunReportsAssistantDraftAction = async ({
  model,
  action,
}: {
  model: ReportGenerationWorkspaceModel;
  action: AssistantResponseAction;
}): Promise<{ accepted: boolean; message: string; details?: string | null }> => {
  const payload = actionPayload(action);
  const operation = payload?.draftOperation;
  if (!operation) {
    return {
      accepted: false,
      message: 'I could not apply that Run Reports action because it did not include a supported draft operation.',
    };
  }

  switch (operation) {
    case 'open-tab': {
      if (payload.tab) {
        model.actions.setActiveTab(payload.tab);
      }
      if (payload.templateView) {
        model.actions.setTemplateView(payload.templateView);
      }
      model.actions.openWorkspace(payload.tab);
      model.actions.setCollapsed(false);
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          `opened the ${payload.tab ?? 'requested'} report-builder panel`,
          `workspace panel is expanded for ${payload.tab ?? 'the selected report section'}`,
        ),
      };
    }
    case 'apply-template': {
      if (!payload.templateId || !payload.source) {
        return { accepted: false, message: 'I could not apply the template because the template id or source was missing.' };
      }
      const templates = payload.source === 'gallery' ? model.galleryTemplates : model.myTemplates;
      const template = templates.find((candidate) => candidate.id === payload.templateId);
      if (!template) {
        return { accepted: false, message: 'That template is no longer available in the Run Reports workspace.' };
      }
      model.actions.setActiveTab('templates');
      model.actions.setTemplateView(payload.source === 'custom' ? 'my' : 'gallery');
      model.actions.applyTemplateById(template.id, payload.source);
      model.actions.setCollapsed(false);
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          `template "${template.name}" applied`,
          `"${template.name}" is selected with ${template.entities.length} entities and ${template.fields.length} fields ready in the report draft`,
        ),
      };
    }
    case 'start-new-report':
      model.actions.startNewReport();
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'new report draft started',
          'workspace is reset for a new local report definition',
        ),
      };
    case 'set-title': {
      const title = String(payload.title ?? '').trim();
      if (!title) {
        return { accepted: false, message: 'I could not set the report title because the title was empty.' };
      }
      model.actions.setReportTitle(title);
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          `report title changed to "${title}"`,
          `workspace title field now targets "${title}"`,
        ),
      };
    }
    case 'select-fields': {
      const fieldIds = Array.isArray(payload.fieldIds) ? payload.fieldIds : [];
      const fields = uniqueFields(fieldIds.flatMap((fieldId) => resolveModelFields(model, fieldId)));
      if (fields.length === 0) {
        return { accepted: false, message: 'I need the Run Reports field catalog before I can change columns. Open the Run Reports workspace and try again.' };
      }
      ensureFieldsSelected(model, fields);
      for (const field of fields) {
        if (model.workspace.hiddenColumns.includes(field.fieldId)) {
          model.actions.toggleHiddenColumn(field.fieldId);
        }
      }
      model.actions.openWorkspace('filters');
      model.actions.setActiveTab('filters');
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          `${fields.length} report columns selected`,
          `filters workspace is open and ${fields.map((field) => field.fieldLabel).slice(0, 4).join(', ')}${fields.length > 4 ? ', and more' : ''} are visible in the draft`,
        ),
      };
    }
    case 'toggle-field': {
      const field = findModelField(model, payload);
      if (!field) {
        return { accepted: false, message: 'That field is not available in this Run Reports workspace.' };
      }
      ensureEntitySelected(model, field.entityId);
      model.actions.toggleField(field.fieldId);
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          `field "${field.fieldLabel}" toggled`,
          `${field.entityLabel} remains selected so the column can be reviewed in the workspace`,
        ),
      };
    }
    case 'add-filter': {
      const applied = payload.filter
        ? applyCondition(model, payload.filter)
        : { blocker: 'The filter payload was missing.' };
      if ('blocker' in applied) {
        return { accepted: false, message: applied.blocker };
      }
      model.actions.openWorkspace('filters');
      model.actions.setActiveTab('filters');
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          `collection filter added on ${applied.field.fieldLabel}`,
          'filters panel is open with the condition applied to the local report draft',
        ),
      };
    }
    case 'update-filter': {
      if (!payload.conditionId) {
        return { accepted: false, message: 'I could not update the filter because the condition id was missing.' };
      }
      model.actions.updateCondition(payload.conditionId, {
        operator: payload.filter?.operator,
        value: payload.filter?.value,
      });
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'report filter updated',
          'filters panel keeps the revised condition in the local report draft',
        ),
      };
    }
    case 'remove-filter': {
      if (!payload.conditionId) {
        return { accepted: false, message: 'I could not remove the filter because the condition id was missing.' };
      }
      model.actions.removeCondition(payload.conditionId);
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'report filter removed',
          'filters panel reflects the local draft condition change',
        ),
      };
    }
    case 'set-date-range': {
      model.actions.updateDateRange(payload.dateRange ?? {});
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'date range updated',
          `workspace date range is set to ${payload.dateRange?.preset ?? 'the requested custom range'}`,
        ),
      };
    }
    case 'set-sort': {
      const field = findModelField(model, payload.sort);
      if (!field || field.sortable !== true) {
        return { accepted: false, message: 'I could not sort by that field because it is not available as a sortable Run Reports field.' };
      }
      model.actions.setPrimarySortField(field);
      if (payload.sort?.direction === 'desc') {
        model.actions.togglePrimarySortDirection();
      }
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          `sort changed to ${field.fieldLabel}`,
          `workspace primary sort is ${payload.sort?.direction ?? 'asc'} for the selected report rows`,
        ),
      };
    }
    case 'clear-selections':
      model.actions.clearAll();
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'report selections cleared',
          'workspace is ready for a new entity and field selection',
        ),
      };
    case 'set-visual': {
      const chartVisualization = model.advancedVisualization?.chartVisualization ?? null;
      const chartType =
        payload.chartType
        ?? chartVisualization?.policy?.defaultChartType
        ?? chartVisualization?.defaultChartType
        ?? chartVisualization?.chartOptions[0]?.chartType
        ?? 'bar';
      const view = payload.view === 'advanced' ? 'advanced' : 'chart';
      const requestedFieldIds = Array.isArray(payload.fieldIds) && payload.fieldIds.length > 0
        ? payload.fieldIds
        : RECOMMENDED_CHART_FIELD_IDS;
      const selectedChartFields = ensureRecommendedChartFields(model, requestedFieldIds);
      model.actions.setBodySurface?.(payload.bodySurface ?? 'visual');
      model.actions.setVisualizationView?.(view);
      model.actions.setVisualizationChartType?.(chartType);
      if (selectedChartFields.length === 0 && !chartVisualization?.chartOptions.length) {
        return {
          accepted: false,
          message: 'I need the Run Reports field catalog before I can choose a useful chart. Open the Run Reports workspace and try again.',
        };
      }
      const fieldSummary = summarizeFieldLabels(selectedChartFields);
      const chartReadiness = visualPolicySupports(model, chartType)
        ? `${chartType} chart is selected from the current visualization policy`
        : `${chartType} chart is selected and will render after the recommended fields refresh the preview`;
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          fieldSummary
            ? `${chartType} visual selected with recommended chart fields (${fieldSummary})`
            : `${chartType} visual selected`,
          `visual surface is open in ${view} mode; ${chartReadiness}`,
        ),
      };
    }
    case 'save-template': {
      if (!model.filterState.filterState.selectedEntities.length || !model.filterState.filterState.selectedFields.length) {
        return { accepted: false, message: 'Select at least one entity and one field before saving this Run Reports template.' };
      }
      if (payload.visibility === 'shared') {
        await model.actions.saveSharedTemplate();
        return {
          accepted: true,
          message: reportBuilderConfirmation(
            'shared template saved',
            `workspace saved ${workspaceContextPhraseFromModel(model)} as a reusable shared definition`,
          ),
        };
      }
      await model.actions.savePrivateTemplate();
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'private template saved',
          `workspace saved ${workspaceContextPhraseFromModel(model)} as a reusable private definition`,
        ),
      };
    }
    case 'schedule-report': {
      if (!model.filterState.filterState.selectedEntities.length || !model.filterState.filterState.selectedFields.length) {
        return { accepted: false, message: 'Select at least one entity and one field before scheduling this Run Reports draft.' };
      }
      await model.actions.toggleDailySchedule();
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'daily schedule updated',
          `workspace schedule is ${model.hasActiveSchedule ? 'toggled off' : 'toggled on'} for ${workspaceContextPhraseFromModel(model)}`,
        ),
      };
    }
    case 'run-export': {
      const format = payload.format ?? 'xlsx';
      if (!['xlsx', 'csv', 'pdf'].includes(format)) {
        return { accepted: false, message: 'That export format is not supported for Run Reports.' };
      }
      await model.actions.runExport(format);
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          `${format.toUpperCase()} export started`,
          `workspace export is running for ${workspaceContextPhraseFromModel(model)}`,
        ),
      };
    }
    case 'run-export-bundle':
      await model.actions.runExportBundle();
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'export bundle started',
          `workspace export is running for ${workspaceContextPhraseFromModel(model)}`,
        ),
      };
    case 'load-history': {
      if (!payload.historyEntryId) {
        return { accepted: false, message: 'I could not load history because the history entry id was missing.' };
      }
      await model.actions.loadHistoryEntry(payload.historyEntryId);
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'history entry loaded',
          'workspace now targets the reusable report definition from history',
        ),
      };
    }
    case 'rerun-history': {
      if (!payload.historyEntryId) {
        return { accepted: false, message: 'I could not rerun history because the history entry id was missing.' };
      }
      if (!model.actions.rerunHistoryEntry) {
        return { accepted: false, message: 'This workspace does not expose a rerun action for history entries.' };
      }
      await model.actions.rerunHistoryEntry(payload.historyEntryId);
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'history rerun started',
          'workspace queued the reusable history definition for rerun',
        ),
      };
    }
    case 'download-history': {
      if (!payload.historyEntryId || !model.actions.downloadHistoryEntry) {
        return { accepted: false, message: 'This history entry cannot be downloaded from the current workspace state.' };
      }
      await model.actions.downloadHistoryEntry(payload.historyEntryId);
      return {
        accepted: true,
        message: reportBuilderConfirmation(
          'history download started',
          'workspace is retrieving the selected history output',
        ),
      };
    }
    default:
      return {
        accepted: false,
        message: `The Run Reports assistant rejected unsupported draft operation "${String(operation)}".`,
      };
  }
};

export const executeRunReportsAssistantPolicyEvent = async ({
  model,
  event,
  activeRouteContext,
}: {
  model: ReportGenerationWorkspaceModel;
  event: AssistantPolicyEvent;
  activeRouteContext: AssistantActiveRouteContextEnvelope;
}): Promise<AssistantPolicyEventResult> => {
  if (event.type !== 'route_local_action') {
    return {
      accepted: false,
      status: 'blocked',
      message: 'Run Reports can only handle route-local assistant policy events.',
      routeContextRef: activeRouteContext.contextRef,
      routeRevision: activeRouteContext.revision,
      presentation: receiptPresentation({
        accepted: false,
        titleText: 'Policy event blocked',
        summary: 'Run Reports can only handle route-local assistant policy events.',
      }),
    };
  }
  if (event.routeContextRef && event.routeContextRef !== activeRouteContext.contextRef) {
    return {
      accepted: false,
      status: 'stale',
      message: 'The assistant action was based on an older Run Reports workspace context. Refresh the route context before applying it.',
      routeContextRef: activeRouteContext.contextRef,
      routeRevision: activeRouteContext.revision,
      presentation: receiptPresentation({
        accepted: false,
        titleText: 'Workspace context changed',
        summary: 'The assistant action was based on an older Run Reports workspace context. Refresh the route context before applying it.',
        meta: `Current revision ${activeRouteContext.revision}`,
      }),
    };
  }
  if (event.routeRevision && event.routeRevision !== activeRouteContext.revision) {
    return {
      accepted: false,
      status: 'stale',
      message: 'The Run Reports workspace changed after the assistant action was generated.',
      routeContextRef: activeRouteContext.contextRef,
      routeRevision: activeRouteContext.revision,
      presentation: receiptPresentation({
        accepted: false,
        titleText: 'Workspace revision changed',
        summary: 'The Run Reports workspace changed after the assistant action was generated.',
        meta: `Current revision ${activeRouteContext.revision}`,
      }),
    };
  }
  const action: AssistantResponseAction = {
    id: event.actionId ?? event.eventId,
    label: 'Apply route action',
    type: 'draft',
    kind: 'draft',
    actionCategory: 'run-reports-workspace-draft',
    payload: event.payload,
  };
  const result = await executeRunReportsAssistantDraftAction({ model, action });
  return {
    accepted: result.accepted,
    status: result.accepted ? 'completed' : 'rejected',
    message: result.message,
    details: result.details ?? null,
    routeContextRef: activeRouteContext.contextRef,
    routeRevision: activeRouteContext.revision,
    presentation: receiptPresentation({
      accepted: result.accepted,
      titleText: result.accepted ? 'Run Reports workspace updated' : 'Run Reports workspace needs attention',
      summary: result.message,
      meta: result.accepted ? (result.details ?? null) : null,
    }),
  };
};
